import { useState } from 'react';
import {
  Box,
  IconButton,
  TextField,
  Avatar,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Skeleton,
  List,
} from '@mui/material';
import {
  Close as CloseIcon,
  Group as GroupIcon,
  InsertDriveFile as FileIcon,
} from '@mui/icons-material';
import { Virtuoso } from 'react-virtuoso';
import { getInitials } from './chatUtils';

const styles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    bgcolor: 'background.paper',
    animation: 'fadeIn 0.3s ease-out',
    '@keyframes fadeIn': {
      from: { opacity: 0 },
      to: { opacity: 1 },
    },
  },
  searchHeader: {
    p: 1.5,
    pb: 0.5,
    display: 'flex',
    alignItems: 'center',
    gap: 1,
  },
  searchInputContainer: {
    position: 'relative',
    flex: 1,
    transition: 'all 0.2s ease',
  },
  searchIcon: {
    position: 'absolute',
    left: 1,
    top: '50%',
    transform: 'translateY(-50%)',
    color: 'action.active',
    transition: 'color 0.2s ease',
  },
  searchInput: {
    '& .MuiOutlinedInput-root': {
      fontSize: "14px",
      color: "#3D4C5E",
      borderRadius: '8px',
      minHeight: "30px",
      transition: 'all 0.2s ease, box-shadow 0.2s ease',
      '&.Mui-focused': {
        boxShadow: '0 0 0 2px rgba(61, 139, 248, 0.15)',
      },
      '&:hover': {
        borderColor: 'primary.main',
      },
    },
    minHeight: "30px"
  },
  filterContainer: {
    p: 1.5,
    display: 'flex',
    gap: 1,
    overflowX: 'auto',
    transition: 'all 0.3s ease',
  },
  topUsers: {
    p: 1.5,
    borderBottom: 1,
    borderColor: 'divider',
  },
  topUsersGrid: {
    display: 'flex',
    gap: 2,
    overflowX: 'auto',
    pb: 1,
  },
  topUserItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minWidth: 64,
    cursor: 'pointer',
    transition: 'transform 0.2s ease, opacity 0.2s ease',
    '&:hover': {
      opacity: 0.8,
      transform: 'translateY(-2px)',
    },
  },
  searchResults: {
    flex: 1,
    overflowY: 'auto',
    py: .5,
    m: 1, mt: 0,
    transition: 'opacity 0.3s ease',
  },
  resultItem: {
    '&:hover': {
      bgcolor: 'rgba(61, 139, 248, 0.1)',
      transform: 'translateY(-4px)',
    },
    cursor: 'pointer',
    margin: "4px 0px",
    padding: "8px 8px",
    borderRadius: '8px',
    transition: 'all 0.15s ease',
    gap: 0
  },
  historyItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderRadius: '8px',
    px: 1,
    py: .5,
    transition: 'all 0.2s ease',
    '&:hover': {
      bgcolor: 'rgba(61, 139, 248, 0.1)',
      transform: 'translateX(4px)',
    },
    cursor: 'pointer',
  },
  closeButton: {
    transition: 'transform 0.2s ease, background-color 0.2s ease',
    '&:hover': {
      transform: 'scale(1.1) rotate(90deg)',
      bgcolor: 'rgba(0, 0, 0, 0.04)',
    },
  },
  listItemAvatar: {
    minWidth: '42px !important',
  },
  avatar: {
    width: 32,
    height: 32,
    fontSize: '14px',
    color: "#212121",
    fontWeight: "500",
    background: "#e5e7eb",
    minWidth: '32px',
    transition: 'transform 0.2s ease',
    '&:hover': {
      transform: 'scale(1.1)',
    }
  },
  listItemContent: {
  },
  skeleton: {
    height: 40,
    width: '100%',
  },
};

const SearchView = ({
  onClose,
  users,
  conversations,
  onSelectConversation,
  searchLoading,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  const handleUserClick = (user) => {
    const existingConversation = conversations.find(
      conv => !conv.isGroup && conv.participants[0].id === user.id
    );

    if (existingConversation) {
      onSelectConversation(existingConversation);
    } else {
      const newConversation = {
        id: user.id,
        title: user.name,
        participants: [user],
        parentUserId: user.parentUserId,
        messages: [],
        unreadCount: 0,
        isGroup: false,
        createdAt: new Date(),
      };
      onSelectConversation(newConversation);
    }
  };

  const getFilteredResults = () => {
    return [...users.filter(user =>
      user.name.toLowerCase().includes(searchQuery.toLowerCase())
    ),
    ];

  };

  const renderSearchResult = (index, result) => {
    if ('role' in result) {
      return (
        <ListItem
          key={result.id}
          sx={styles.resultItem}
          onClick={() => handleUserClick(result)}
        >
          <ListItemAvatar sx={styles.listItemAvatar}>
            <Avatar sx={styles.avatar}>{getInitials(result.name)}</Avatar>
          </ListItemAvatar>
          <ListItemText
            primary={result.name}
            sx={styles.listItemContent}
          />
        </ListItem>
      );
    } else if ('isGroup' in result) {
      return (
        <ListItem
          key={result.id}
          sx={styles.resultItem}
          onClick={() => onSelectConversation(result)}
        >
          <ListItemAvatar>
            <Avatar sx={{
              bgcolor: 'primary.main',
              ...styles.avatar
            }}>
              <GroupIcon />
            </Avatar>
          </ListItemAvatar>
          <ListItemText
            primary={result.title}
            secondary={
              result.participants.slice(0, 2).map((p) => p.name).join(', ') +
              (result.participants.length > 2 ? ` +${result.participants.length - 2}` : '')
            }
            sx={styles.listItemContent}
          />
        </ListItem>
      );
    } else {
      return (
        <ListItem
          key={result.id}
          sx={styles.resultItem}
          onClick={() => {
            const conversation = conversations.find(conv =>
              conv.messages.some(msg => msg.id === result.id)
            );
            if (conversation) {
              onSelectConversation(conversation);
            }
          }}
        >
          <ListItemAvatar>
            <Avatar sx={{
              bgcolor: 'grey.100',
              ...styles.avatar
            }}>
              <FileIcon color="action" />
            </Avatar>
          </ListItemAvatar>
          <ListItemText
            primary={result.content}
            secondary={result.timestamp.toLocaleDateString()}
            sx={styles.listItemContent}
          />
        </ListItem>
      );
    }
  };

  const filteredResults = getFilteredResults();

  const SearchSkeleton = () => {
    return (
      <Box sx={styles.searchResults}>
        <List>
          {[...Array(4)].map((_, index) => (
            <ListItem
              key={index}
              sx={{ m: 0, p: 0, pb: 1 }}
            >
              <Skeleton sx={styles.skeleton} variant="rounded" />
            </ListItem>
          ))}
        </List>
      </Box>
    );
  }

  return (
    <Box sx={styles.root}>
      <Box sx={styles.searchHeader}>
        <TextField
          fullWidth
          size="small"
          value={searchQuery}
          onChange={(e) => handleSearch(e.target.value)}
          placeholder="Search..."
          sx={styles.searchInput}
          autoFocus
          autoComplete="off"
        />
        <IconButton size="small" onClick={onClose} sx={styles.closeButton}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={styles.searchResults}>
        {searchLoading ? (
          <SearchSkeleton />
        ) : (
          <Virtuoso
            style={{ height: '100%' }}
            data={filteredResults}
            itemContent={renderSearchResult}
            totalCount={filteredResults.length}
            overscan={20}
          />
        )}
      </Box>
    </Box>
  );
};
export default SearchView;